import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users, Building2, Tag, Store, Briefcase, User as UserIcon,
  Activity, BrainCircuit, MessageSquare, X
} from 'lucide-react';
import SmartAssistancePanel from './SmartAssistancePanel';
import ChatbotPanel from './ChatbotPanel';
import { API, apiFetch } from '../lib/api';

const REFRESH_MS = 60000;


function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Bottom-sticky status bar — live stats + timezone clock + Smart Assist + Chatbot */
export default function StatusBar({ user }) {
  const [stats, setStats] = useState(null);
  const now = useNow(1000);
  const [panel, setPanel] = useState(null); // null | 'assist' | 'chat'
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/licensor/live-stats`);
      if (res.ok) setStats(await res.json());
    } catch (_) {}
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Close panel on outside click
  useEffect(() => {
    if (!panel) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setPanel(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [panel]);

  const fmt = (n) => n == null ? '—' : Number(n).toLocaleString();

  // Timezone label
  const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzShort = (() => {
    try {
      return new Intl.DateTimeFormat([], { timeZoneName: 'short', timeZone: tzName })
        .formatToParts(now).find(p => p.type === 'timeZoneName')?.value || '';
    } catch { return ''; }
  })();

  return (
    <>
      {/* Smart Assist / Chatbot panel (above status bar) */}
      {panel && (
        <div ref={panelRef} style={{
          position: 'fixed', bottom: 28, left: 0, right: 0,
          height: 460, zIndex: 1000,
          display: 'flex', flexDirection: 'column',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderBottom: 'none', boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
          animation: 'panel-rise 180ms ease'
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 14px', borderBottom: '1px solid var(--border)',
            background: 'var(--surface-3)', flexShrink: 0
          }}>
            <div style={{ display: 'flex', gap: 2 }}>
              <PanelTab id="assist" active={panel} label="Smart Assist" icon={<BrainCircuit size={12} />} onClick={setPanel} />
              <PanelTab id="chat"   active={panel} label="AI Chatbot"   icon={<MessageSquare size={12} />} onClick={setPanel} />
            </div>
            <button
              onClick={() => setPanel(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px 6px', borderRadius: 4 }}
              title="Close panel"
            >
              <X size={14} />
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {panel === 'assist' && <SmartAssistancePanel />}
            {panel === 'chat'   && <ChatbotPanel />}
          </div>
        </div>
      )}

      <div className="status-bar" data-testid="status-bar" role="status" aria-live="polite">
        <Item icon={<UserIcon size={11} />}   label="User"       value={user?.username || 'Guest'} testid="status-user" />
        <Item icon={<Activity size={11} />}   label="Online"     value={fmt(stats?.onlineUsers)} testid="status-online" highlight />
        <Item icon={<Briefcase size={11} />}  label="Clients"    value={fmt(stats?.clients)} testid="status-clients" />
        <Item icon={<Tag size={11} />}        label="Licensed"   value={fmt(stats?.licensed)} testid="status-licensed" />
        <Item icon={<Users size={11} />}      label="Users"      value={fmt(stats?.users)} testid="status-users" />
        <Item icon={<Store size={11} />}      label="Stores"     value={fmt(stats?.stores)} testid="status-stores" />
        <Item icon={<Building2 size={11} />}  label="Industries" value={fmt(stats?.industries)} testid="status-industries" />

        <div className="status-bar-spacer" />

        {/* Smart Assist icon */}
        <button
          onClick={() => setPanel(p => p === 'assist' ? null : 'assist')}
          data-testid="status-smart-assist-btn"
          title="Smart Assistance — DB Explorer & AI"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', background: panel === 'assist' ? 'rgba(59,130,246,0.15)' : 'none',
            border: 'none', cursor: 'pointer', borderRadius: 4,
            color: panel === 'assist' ? '#3b82f6' : 'var(--text-secondary)',
            fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
            transition: 'background 120ms, color 120ms'
          }}
        >
          <BrainCircuit size={12} />
          <span className="guard-pill-label">Assist</span>
        </button>

        {/* Chatbot icon */}
        <button
          onClick={() => setPanel(p => p === 'chat' ? null : 'chat')}
          data-testid="status-chatbot-btn"
          title="AI Chatbot — ERP Assistant & Client Messaging"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', background: panel === 'chat' ? 'rgba(16,185,129,0.15)' : 'none',
            border: 'none', cursor: 'pointer', borderRadius: 4,
            color: panel === 'chat' ? '#10b981' : 'var(--text-secondary)',
            fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
            transition: 'background 120ms, color 120ms'
          }}
        >
          <MessageSquare size={12} />
          <span className="guard-pill-label">Chat</span>
        </button>

        {/* Datetime with timezone */}
        <div className="status-bar-item" data-testid="status-datetime" style={{ borderRight: 0, gap: 4 }}>
          <span className="status-bar-value" style={{ fontFamily: 'var(--font-mono, monospace)' }}>
            {now.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric', timeZone: tzName })}
          </span>
          <span className="status-bar-value" style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700 }}>
            {now.toLocaleTimeString([], { hour12: false, timeZone: tzName })}
          </span>
          {tzShort && (
            <span style={{ fontSize: 9, color: 'var(--statusbar-muted)', opacity: 0.7, fontWeight: 600 }}>{tzShort}</span>
          )}
        </div>
      </div>
    </>
  );
}

function PanelTab({ id, active, label, icon, onClick }) {
  const isActive = active === id;
  return (
    <button
      onClick={() => onClick(id)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 12px', fontSize: 11, fontWeight: 700,
        border: 'none', cursor: 'pointer', borderRadius: 4,
        background: isActive ? 'var(--surface)' : 'transparent',
        color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
        boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
        transition: 'background 120ms'
      }}
    >{icon}{label}</button>
  );
}

function Item({ icon, label, value, highlight, testid }) {
  return (
    <div className="status-bar-item" data-testid={testid}>
      <span style={{ color: 'var(--statusbar-muted)' }}>{icon}</span>
      <span className="status-bar-label">{label}</span>
      <span className="status-bar-value" style={highlight ? { color: 'var(--green, #107C10)' } : undefined}>
        {value}
      </span>
    </div>
  );
}
